import {
  Spine,
  SpineItem,
  SpineToggle,
  SpineItemClickEvent,
  SpineEvent,
} from '@wk/components-v3-react16';
import { useResetAtom } from 'jotai/utils';
import { useAppSelector } from 'redux/hooks';
import CsbErrorBoundary from 'components/CsbErrorBoudary/CsbErrorBoundary';
import { memo, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  NOTIFICATION_BOX_MESSAGES,
  SPINE_MENU_ITEMS,
  USER_PERMISSIONS,
} from 'utils/common-constants';
import style from './SpineMenuCsb.module.scss';
import { selectUserPermissions } from 'redux/auth/AuthSlice';
import { hasPermission } from 'utils/commonFunctions/CommonFunctions';
import AuthErrorNotification from 'components/AuthErrorNotification/AuthErrorNotification';
import { usePostLogoutMutation } from 'api/auth/authApi';
import { workflowAtom } from 'pages/NewDesignOrchestration/atoms';

const SpineMenuCsb = memo((properties: any) => {
  const userPermission = useAppSelector(selectUserPermissions);
  const loc = useLocation();
  const [logout] = usePostLogoutMutation();
  const navigate = useNavigate();
  const resetWorkflow = useResetAtom(workflowAtom);

  const treeId = 'csb-spine-tree';
  const dashboardPath = '/dashboard';
  const menuItems = [
    {
      activeIcon: 'wk-icon-filled-list-bullet-check',
      enabled: true,
      href: dashboardPath,
      icon: 'wk-icon-list-bullet-check',
      id: SPINE_MENU_ITEMS.VIEW_ORCHESTRATION,
      permission: [USER_PERMISSIONS.ORCHESTRATIONS_RUN_HISTORY_PAGE],
      text: 'Orchestration Status',
    },
    {
      activeIcon: 'wk-icon-filled-play',
      enabled: true,
      href: '/run-orchestrations',
      icon: 'wk-icon-play',
      id: SPINE_MENU_ITEMS.RUN_ORCHESTRATION,
      permission: [USER_PERMISSIONS.ORCHESTRATION_RUN_PAGE],
      text: 'Run Orchestration',
    },
    {
      activeIcon: 'wk-icon-filled-pencil',
      enabled: true,
      href: '/design-orchestration',
      icon: 'wk-icon-pencil',
      id: SPINE_MENU_ITEMS.EDIT_ORCHESTRATION,
      permission: [USER_PERMISSIONS.DESIGNER_PAGE],
      text: 'Design Orchestration',
    },
    {
      activeIcon: 'wk-icon-filled-cubes',
      enabled: true,
      href: '/manage-modules',
      icon: 'wk-icon-cubes',
      id: SPINE_MENU_ITEMS.MANAGE_MODULES,
      permission: [USER_PERMISSIONS.MODULES_PAGE],
      text: 'Manage Modules',
    },
  ];

  const visibleItems = menuItems.filter(({ permission, enabled }) => {
    if (enabled === false) {
      return false;
    }

    return hasPermission(userPermission, permission);
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const onToggleButtonClick = useCallback((event: SpineEvent) => {
    setIsExpanded(!!event.isExpanded);
  }, []);

  const onItemClick = useCallback(
    (event: SpineItemClickEvent) => {
      if (event?.href === '/design-orchestration') {
        resetWorkflow();
      }
      navigate(event?.href || dashboardPath);
    },
    [navigate, resetWorkflow]
  );

  return (
    <CsbErrorBoundary>
      <div className={style['spine-menu-container']} data-testid="SpineMenuCsb">
        {visibleItems.length > 0 ? (
          <>
            <div className={style['spine-navigation']}>
              <Spine
                isExpanded={isExpanded}
                treeId={treeId}
                onUserRequest={onToggleButtonClick}
              >
                {visibleItems.map((item, index) => (
                  <SpineItem
                    key={item.id}
                    data-testid={item.id}
                    slot="spineItem"
                    href={item.href}
                    label={item.text}
                    isSelected={loc.pathname.includes(item.href) ?? index === 0}
                    iconName={item.icon}
                    activeIconName={item.activeIcon}
                    onItemClick={onItemClick}
                  />
                ))}

                <SpineToggle
                  slot="spineToggleItem"
                  srControls={treeId}
                  isExpanded={isExpanded}
                  onUserRequest={onToggleButtonClick}
                />
              </Spine>
            </div>
            <div className={style['spine-content']}>{properties.children}</div>
          </>
        ) : (
          <AuthErrorNotification
            authError={NOTIFICATION_BOX_MESSAGES.PERMISSION_ERROR}
            isLoading={false}
            handleLogout={() => logout(null)}
          />
        )}
      </div>
    </CsbErrorBoundary>
  );
});

export default SpineMenuCsb;
